/**
 * PDF export with pdfmake (MIT), loaded on demand (ADR 0018). Embedded Noto Sans (SIL OFL 1.1, Latin
 * subset with Turkish letters: public/fonts). A4 landscape, neutral header "FTS — <report>", filter
 * summary, generated at/by, the route map snapshot when given, tables with repeated header rows and page
 * numbers. A PDF is for reading: each table is capped at PDF_ROW_CAP rows and says so.
 */
import type { Content, TDocumentDefinitions, TableCell } from "pdfmake/interfaces";
import { PDF_ROW_CAP, cellText, fill, formatDateTimeIn, type ExportMeta, type Sheet } from "./sheet";

type PdfMake = {
  createPdf: (
    dd: TDocumentDefinitions,
    tableLayouts?: unknown,
    fonts?: Record<string, Record<string, string>>,
    vfs?: Record<string, string>,
  ) => { getBuffer: (cb: (buf: Uint8Array) => void) => void };
};

async function loadPdfMake(): Promise<PdfMake> {
  const mod = (await import("pdfmake/build/pdfmake")) as unknown as PdfMake & { default?: PdfMake };
  return mod.default && typeof mod.default.createPdf === "function" ? mod.default : mod;
}

/** Base64 of a binary buffer, without Node's Buffer (browser, worker, Node ≥ 16). */
export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const FONT = "NotoSans";

export function pdfDefinition(sheets: Sheet[], meta: ExportMeta, mapImage?: string | null, compress = true): TDocumentDefinitions {
  const content: Content[] = [];
  const title = `FTS — ${meta.reportName}`;
  content.push({ text: title, fontSize: 15, bold: true, margin: [0, 0, 0, 6] });
  const infoRows: TableCell[][] = [
    ...meta.filters.map(([k, v]) => [{ text: k, bold: true }, v] as TableCell[]),
    [{ text: meta.labels.timeZone, bold: true }, meta.tz],
    [{ text: meta.labels.generatedAt, bold: true }, formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale)],
    [{ text: meta.labels.generatedBy, bold: true }, meta.generatedBy],
  ];
  content.push({ table: { widths: [120, "*"], body: infoRows }, layout: "noBorders", fontSize: 8, margin: [0, 0, 0, 10] });
  if (mapImage) content.push({ image: mapImage, fit: [785, 300], alignment: "center", margin: [0, 0, 0, 10] });
  else if (mapImage === null) content.push({ text: meta.labels.mapUnavailable, italics: true, color: "#555555", margin: [0, 0, 0, 10] });

  for (const s of sheets) {
    if (sheets.length > 1) content.push({ text: s.name, fontSize: 11, bold: true, margin: [0, 6, 0, 4] });
    if (s.rows.length === 0) {
      content.push({ text: meta.labels.noRows, italics: true, margin: [0, 0, 0, 8] });
      continue;
    }
    const rows = s.rows.slice(0, PDF_ROW_CAP);
    const body: TableCell[][] = [
      s.columns.map((c) => ({ text: c.header, bold: true, fillColor: "#eeeeee" })),
      ...rows.map((r) =>
        s.columns.map((c, j) => {
          const text = cellText(r[j] ?? null, c.type, meta, meta.locale === "tr");
          return c.type === "text" || c.type === "bool" || c.type === "datetime" || c.type === "date" ? text : { text, alignment: "right" as const };
        }),
      ),
    ];
    content.push({
      table: { headerRows: 1, widths: s.columns.map(() => "auto"), body, dontBreakRows: true },
      layout: "lightHorizontalLines",
      fontSize: 7,
      margin: [0, 0, 0, 8],
    });
    if (s.rows.length > rows.length) {
      content.push({ text: fill(meta.labels.pdfRowCap, { shown: rows.length, total: s.rows.length }), italics: true, margin: [0, 0, 0, 8] });
    }
  }

  return {
    pageSize: "A4",
    pageOrientation: "landscape",
    compress,
    pageMargins: [28, 40, 28, 34],
    info: { title, creator: "FTS", producer: "FTS" },
    header: () => ({
      columns: [
        { text: title, bold: true, fontSize: 8 },
        { text: formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale), alignment: "right", fontSize: 8 },
      ],
      margin: [28, 16, 28, 0],
    }),
    footer: (page: number, pages: number) => ({ text: fill(meta.labels.page, { page, pages }), alignment: "center", fontSize: 8, margin: [0, 10, 0, 0] }),
    content,
    defaultStyle: { font: FONT, fontSize: 8 },
  };
}

export async function buildPdf(
  sheets: Sheet[],
  meta: ExportMeta,
  fonts: { regular: ArrayBuffer; bold: ArrayBuffer },
  mapImage?: string | null,
  opts: { compress?: boolean } = {},
): Promise<Uint8Array> {
  const pdfMake = await loadPdfMake();
  const vfs = { "NotoSans-Regular.ttf": toBase64(fonts.regular), "NotoSans-Bold.ttf": toBase64(fonts.bold) };
  const fontDefs = {
    [FONT]: { normal: "NotoSans-Regular.ttf", bold: "NotoSans-Bold.ttf", italics: "NotoSans-Regular.ttf", bolditalics: "NotoSans-Bold.ttf" },
  };
  const doc = pdfMake.createPdf(pdfDefinition(sheets, meta, mapImage, opts.compress ?? true), undefined, fontDefs, vfs);
  return new Promise<Uint8Array>((resolve, reject) => {
    try {
      doc.getBuffer((b) => resolve(b));
    } catch (e) {
      reject(e);
    }
  });
}
